
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/submit/ministry-contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 px-6 lg:px-12 bg-[#F5E6D3]">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="w-16 h-1 bg-[#D4A574] mx-auto mb-4"></div>
            <h2 className="text-5xl md:text-6xl font-bold text-[#2C1810] font-['Space_Mono'] tracking-tight">
              GET IN TOUCH
            </h2>
            <div className="w-16 h-1 bg-[#D4A574] mx-auto mt-4"></div>
          </div>
          <p className="text-base text-[#5C4033] max-w-2xl mx-auto font-['DM_Sans'] mt-6 mb-8 tracking-wide">
            WE'D LOVE TO HEAR FROM YOU
          </p>
          <div className="border-4 border-[#2C1810] p-6 max-w-3xl mx-auto bg-white">
            <p className="text-sm text-[#2C1810] leading-relaxed font-['Crimson_Text'] italic">
              "Come to me, all you who are weary and burdened, and I will give you rest."
            </p>
            <p className="text-xs text-[#D4A574] font-['Space_Mono'] mt-3 tracking-wider">
              — MATTHEW 11:28
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6 border-4 border-[#2C1810] p-8 bg-white" data-readdy-form id="ministry-contact-form">
            <div>
              <label className="block text-xs font-['Space_Mono'] text-[#2C1810] mb-2 tracking-wider">
                YOUR NAME
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm bg-[#F5E6D3]"
              />
            </div>

            <div>
              <label className="block text-xs font-['Space_Mono'] text-[#2C1810] mb-2 tracking-wider">
                EMAIL ADDRESS
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm bg-[#F5E6D3]"
              />
            </div>

            <div>
              <label className="block text-xs font-['Space_Mono'] text-[#2C1810] mb-2 tracking-wider">
                PHONE NUMBER (OPTIONAL)
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm bg-[#F5E6D3]"
              />
            </div>

            <div>
              <label className="block text-xs font-['Space_Mono'] text-[#2C1810] mb-2 tracking-wider">
                YOUR MESSAGE
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] resize-none font-['DM_Sans'] text-sm bg-[#F5E6D3]"
              />
              <div className="text-xs text-[#5C4033] mt-2 text-right font-['Space_Mono']">
                {formData.message.length}/500
              </div>
            </div>

            {status === 'success' && (
              <div className="bg-[#D4A574] border-2 border-[#2C1810] text-[#2C1810] px-4 py-3 font-['DM_Sans'] text-sm">
                Thank you! Your message has been sent successfully.
              </div>
            )}

            {status === 'error' && (
              <div className="bg-[#2C1810] border-2 border-[#2C1810] text-[#F5E6D3] px-4 py-3 font-['DM_Sans'] text-sm">
                Something went wrong. Please try again.
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full px-8 py-4 bg-[#2C1810] text-[#F5E6D3] font-bold font-['Space_Mono'] hover:bg-[#D4A574] hover:text-[#2C1810] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide border-2 border-[#2C1810]"
            >
              {status === 'submitting' ? 'SENDING...' : 'SEND MESSAGE'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
